import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { jsPDF } from 'jspdf';
import '../styles/QuickSaleForm.css';

const SupervisorQuickSaleForm = ({ onSaleAdded, onCancel, supervisorInfo }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    sale_date: new Date().toISOString().split('T')[0], // Fecha actual por defecto
    total_amount: '',
    payment_method: 'Efectivo', // Valor por defecto
    point_of_sale_id: '',
    description: ''
  });
  const [printTicket, setPrintTicket] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [loadingPointsOfSale, setLoadingPointsOfSale] = useState(true);
  const [isGeneratingTicket, setIsGeneratingTicket] = useState(false);
  const [businessDateTime, setBusinessDateTime] = useState(null);

  // Función para obtener la fecha y hora del negocio
  const fetchBusinessTime = async (businessId) => {
    if (!businessId) return null;
    
    try {
      console.log('Obteniendo fecha y hora del negocio para el registro de venta...');
      const serverTimeResponse = await axios.get('/api/sales/server-time', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        timeout: 5000
      });
      
      console.log('Respuesta de fecha/hora del negocio:', serverTimeResponse.data);
      
      // La función get_business_time devuelve la fecha y hora ya ajustada al huso horario del negocio
      const serverTimeStr = serverTimeResponse.data.time;
      
      if (serverTimeStr && typeof serverTimeStr === 'string') {
        // Guardar la fecha/hora completa del negocio
        setBusinessDateTime(serverTimeStr);
        console.log('Fecha/hora completa del negocio para registro en BD:', serverTimeStr);
        return serverTimeStr;
      }
      return null;
    } catch (error) {
      console.error('Error al obtener la fecha/hora del negocio:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchPointsOfSale = async () => {
      try {
        setLoadingPointsOfSale(true);
        const token = localStorage.getItem('token');
        
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        // Para supervisores, obtener puntos de venta disponibles
        const response = await axios.get('/api/points-of-sale/available', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setPointsOfSale(response.data);
        
        // Lógica específica para supervisores
        if (supervisorInfo) {
          if (!supervisorInfo.is_general_supervisor && supervisorInfo.pos_id) {
            // Supervisor específico: fijar el POS asignado
            const assignedPOS = response.data.find(pos => pos.id === supervisorInfo.pos_id);
            if (assignedPOS) {
              setFormData(prevState => ({
                ...prevState,
                point_of_sale_id: assignedPOS.id
              }));
              
              // Obtener la fecha y hora del negocio
              if (assignedPOS.business_id) {
                await fetchBusinessTime(assignedPOS.business_id);
              }
            }
          } else if (supervisorInfo.is_general_supervisor && response.data.length > 0) {
            // Supervisor general: NO seleccionar automáticamente, dejar vacío para que pueda elegir
            // Solo obtener la fecha y hora del primer negocio disponible
            if (response.data[0].business_id) {
              await fetchBusinessTime(response.data[0].business_id);
            }
          }
        } else {
          // Fallback para casos sin supervisorInfo
          if (response.data.length === 1) {
            setFormData(prevState => ({
              ...prevState,
              point_of_sale_id: response.data[0].id
            }));
          } else if (response.data.length > 0) {
            // No seleccionar automáticamente si hay múltiples opciones
            if (response.data[0].business_id) {
              await fetchBusinessTime(response.data[0].business_id);
            }
          }
        }
      } catch (error) {
        console.error('Error al obtener puntos de venta:', error);
        setError('Error al cargar los puntos de venta. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingPointsOfSale(false);
      }
    };

    fetchPointsOfSale();
  }, [user, supervisorInfo]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      if (name === 'printTicket') {
        setPrintTicket(checked);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Función para generar el ticket de venta en PDF con la zona horaria del negocio
  const generateTicket = async (saleData) => {
    try {
      console.log('%c Iniciando generación de ticket PDF...', 'background: #222; color: #bada55; font-size: 14px');
      setIsGeneratingTicket(true);
      
      // Obtener la hora del servidor desde el backend con la zona horaria del negocio
      console.log('INICIO: Obteniendo hora del servidor desde el backend');
      
      let formattedDate = '';
      let formattedTime = '';
      let businessTimezone = 'America/Mexico_City';
      
      try {
        const serverTimeResponse = await axios.get('/api/sales/server-time', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 3000
        });
        
        const serverTimeStr = serverTimeResponse.data.time;
        businessTimezone = serverTimeResponse.data.timezone || 'America/Mexico_City';
        
        // Procesar la fecha y hora del servidor
        if (typeof serverTimeStr === 'string') {
          const dateTimeParts = serverTimeStr.split('T');
          if (dateTimeParts.length === 2) {
            const datePart = dateTimeParts[0];
            let timePart = dateTimeParts[1].split(/[+-]|Z/)[0];
            
            const [year, month, day] = datePart.split('-');
            formattedDate = `${day}/${month}/${year}`;
            
            const timeWithoutMillis = timePart.split('.')[0];
            formattedTime = timeWithoutMillis;
            if (timeWithoutMillis.split(':').length === 2) {
              formattedTime = `${timeWithoutMillis}:00`;
            }
          }
        }
      } catch (timeError) {
        console.error('Error al obtener hora del servidor, usando hora local:', timeError);
        // Usar hora local como respaldo
        const now = new Date();
        formattedDate = now.toLocaleDateString('es-MX');
        formattedTime = now.toLocaleTimeString('es-MX', { hour12: false });
      }
      
      // Obtener datos del negocio y usuario usando la misma implementación que venta detallada
      let business = {};
      let currentUser = {};
      
      // Obtener business_id del punto de venta seleccionado
      const selectedPointOfSale = pointsOfSale.find(pos => pos.id === saleData.pointOfSaleId);
      
      if (selectedPointOfSale && selectedPointOfSale.business_id) {
        try {
          console.log('🔍 Llamando a /api/businesses/' + selectedPointOfSale.business_id + '...');
          const businessDetailResponse = await axios.get(`/api/businesses/${selectedPointOfSale.business_id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 5000
          });
          
          if (businessDetailResponse.data) {
            business = businessDetailResponse.data;
            console.log('✅ Business obtenido:', business);
          }
        } catch (businessError) {
          console.error('❌ Error al obtener datos del negocio:', businessError);
        }
      }
      
      try {
        console.log('🔍 Llamando a /api/users/me...');
        const userResponse = await axios.get('/api/users/me', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 3000
        });
        currentUser = userResponse.data;
        console.log('✅ Usuario obtenido:', currentUser);
      } catch (userError) {
        console.error('❌ Error al obtener datos del usuario:', userError);
        currentUser = { full_name: 'Supervisor' };
      }
      
      const pointOfSale = selectedPointOfSale;
      const user = currentUser;
      
      // Crear el documento PDF con ancho de 58mm (ticket pequeño)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [58, 200]
      });
      
      doc.setFont('helvetica');
      doc.setFontSize(10);
      
      // Encabezado del ticket
      doc.text(business.bis_name || 'Negocio', 29, 10, null, null, 'center');
      doc.text('Ticket de Venta', 29, 15, null, null, 'center');
      doc.text(`Tel: ${business.bis_phone || 'No disponible'}`, 29, 20, null, null, 'center');
      doc.text('Venta Rápida - Supervisor', 29, 25, null, null, 'center');
      
      // Información de la venta
      const displayPosName = saleData.pointOfSaleName || (pointOfSale && pointOfSale.pos_name ? pointOfSale.pos_name : 'No disponible');
      
      doc.text(`Punto de venta: ${displayPosName}`, 2, 35);
      const userName = user && user.full_name ? user.full_name.substring(0, 12) : 'No disponible';
      doc.text(`Supervisor: ${userName}`, 2, 40);
      doc.text(`Fecha: ${formattedDate || 'No disponible'}`, 2, 45);
      doc.text(`Hora: ${formattedTime || 'No disponible'}`, 2, 50);
      
      // Línea divisoria
      doc.line(2, 60, 56, 60);
      doc.setFontSize(9);
      doc.text('Detalle de la venta', 29, 65, null, null, 'center');
      doc.setFontSize(8);
      doc.text('Producto', 2, 70);
      doc.text('Total', 52, 70, null, null, 'right');
      
      // Línea divisoria
      doc.line(2, 75, 56, 75);
      
      // Productos (venta rápida)
      let y = 80;
      doc.text('Venta rápida', 2, y);
      doc.text(`$${saleData.total.toFixed(2)}`, 52, y, null, null, 'right');
      y += 5;
      
      // Línea divisoria
      doc.line(2, y, 56, y);
      y += 5;
      
      // Totales
      const subtotal = saleData.total;
      
      doc.text(`Subtotal: $${subtotal.toFixed(2)}`, 52, y, null, null, 'right');
      y += 5;
      y += 5;
      doc.setFontSize(10);
      doc.text(`Total: $${saleData.total.toFixed(2)}`, 52, y, null, null, 'right');
      doc.setFontSize(8);
      y += 10;
      
      // Método de pago
      doc.text(`Método de pago: ${saleData.paymentMethod || 'Efectivo'}`, 2, y);
      y += 5;
      
      // Pie de página
      y += 10;
      doc.text('Gracias por su compra', 29, y, null, null, 'center');
      y += 5;
      
      // Crear ID de venta único
      const dateTimeStr = formattedDate.replace(/\//g, '') + formattedTime.replace(/:/g, '');
      let idPosName = 'POS';
      if (pointOfSale && pointOfSale.pos_name) {
        idPosName = pointOfSale.pos_name.replace(/\s+/g, '');
      } else if (saleData.pointOfSaleName) {
        idPosName = saleData.pointOfSaleName.replace(/\s+/g, '');
      }
      const userNameStr = userName.replace(/\s+/g, '');
      const totalInt = Math.floor(saleData.total);
      const saleIdUnique = `${dateTimeStr}${idPosName}${userNameStr}${totalInt}`;
      
      doc.text('IdVenta:', 2, y);
      y += 5;
      
      // Dividir el ID de venta en múltiples líneas de 37 caracteres cada una
      if (saleIdUnique.length > 37) {
        const numLines = Math.ceil(saleIdUnique.length / 37);
        
        for (let i = 0; i < numLines; i++) {
          const startIndex = i * 37;
          const endIndex = Math.min(startIndex + 37, saleIdUnique.length);
          const linePart = saleIdUnique.substring(startIndex, endIndex);
          
          doc.text(linePart, 2, y);
          
          if (i < numLines - 1) {
            y += 5;
          }
        }
      } else {
        doc.text(saleIdUnique, 2, y);
      }
      
      // Generar el nombre del archivo con la fecha y hora
      const dateForFilename = formattedDate.replace(/\//g, '-');
      const timeForFilename = formattedTime.replace(/:/g, '-').replace(/\s/g, '');
      const filename = `ticket_venta_supervisor_${dateForFilename}_${timeForFilename}.pdf`;
      
      // Guardar el PDF
      doc.save(filename);
      
      setIsGeneratingTicket(false);
      return true;
    } catch (error) {
      console.error('Error al generar el ticket:', error);
      setIsGeneratingTicket(false);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
        return;
      }

      // Validar campos requeridos
      if (!formData.sale_date || !formData.total_amount || !formData.point_of_sale_id || !formData.payment_method) {
        setError('Todos los campos son obligatorios');
        setLoading(false);
        return;
      }

      // Validar que el monto total sea un número positivo
      if (isNaN(formData.total_amount) || parseFloat(formData.total_amount) <= 0) {
        setError('El monto total debe ser un número positivo');
        setLoading(false);
        return;
      }

      // Actualizar la fecha y hora del negocio antes de enviar
      let currentBusinessTime = businessDateTime;
      const selectedPointOfSale = pointsOfSale.find(pos => pos.id === formData.point_of_sale_id);
      
      if (selectedPointOfSale?.business_id) {
        const freshBusinessTime = await fetchBusinessTime(selectedPointOfSale.business_id);
        if (freshBusinessTime) {
          currentBusinessTime = freshBusinessTime;
        }
      }
      
      // Combinar la fecha seleccionada por el usuario con la zona horaria del negocio
      let finalSaleDate = formData.sale_date;
      
      try {
        if (currentBusinessTime && formData.sale_date) {
          const [userYear, userMonth, userDay] = formData.sale_date.split('-').map(Number);
          const businessDate = new Date(currentBusinessTime);
          
          const combinedDate = new Date(Date.UTC(
            userYear,
            userMonth - 1,
            userDay,
            businessDate.getUTCHours(),
            businessDate.getUTCMinutes(),
            businessDate.getUTCSeconds(),
            businessDate.getUTCMilliseconds()
          ));
          
          finalSaleDate = combinedDate.toISOString();
        }
      } catch (dateError) {
        console.error('Error al combinar fecha del usuario con zona horaria del negocio:', dateError);
      }
      
      // Enviar datos al servidor
      const response = await axios.post('/api/sales/quick', 
        {
          sale_date: finalSaleDate,
          total_amount: parseFloat(formData.total_amount),
          payment_method: formData.payment_method,
          point_of_sale_id: formData.point_of_sale_id,
          description: formData.description && formData.description.trim() !== '' ? formData.description.trim() : null
        }, 
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Limpiar el formulario
      setFormData({
        sale_date: new Date().toISOString().split('T')[0],
        total_amount: '',
        payment_method: 'Efectivo',
        point_of_sale_id: formData.point_of_sale_id, // Mantener el punto de venta seleccionado
        description: ''
      });

      console.log('%c Venta registrada exitosamente por supervisor', 'background: green; color: white; font-size: 14px');
      
      // Si la casilla de imprimir ticket está marcada
      if (printTicket) {
        setError(null);
        setSuccessMessage('Venta registrada. Generando ticket...');
        
        setTimeout(async () => {
          try {
            const saleId = response.data.id || 'N/A';
            const selectedPointOfSale = pointsOfSale.find(pos => pos.id === formData.point_of_sale_id);
            const pointOfSaleName = selectedPointOfSale ? selectedPointOfSale.pos_name : 'No disponible';
            
            const saleData = {
              pointOfSaleId: formData.point_of_sale_id,
              pointOfSaleName: pointOfSaleName,
              paymentMethod: formData.payment_method,
              total: parseFloat(formData.total_amount),
              products: [],
              saleId: saleId
            };
            
            await generateTicket(saleData);
            
            setTimeout(() => {
              onSaleAdded();
            }, 2000);
          } catch (ticketError) {
            console.error('Error al generar ticket desde setTimeout:', ticketError);
            setError('Error al generar el ticket: ' + ticketError.message);
          }
        }, 500);
      } else {
        onSaleAdded();
      }
    } catch (error) {
      console.error('Error al registrar la venta:', error);
      setError(error.response?.data?.message || 'Error al registrar la venta. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Determinar si el selector de POS debe estar deshabilitado
  const isPOSDisabled = () => {
    // Solo deshabilitar para supervisores específicos (no generales)
    if (supervisorInfo && !supervisorInfo.is_general_supervisor) return true;
    return false; // Siempre permitir selección para supervisores generales o mientras carga
  };

  return (
    <div className="quick-sale-form-container">
      <h3>Venta Rápida - Supervisor</h3>
      {error && <div className="alert alert-danger">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      <form className="quick-sale-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="point_of_sale_id">Punto de Venta *</label>
          <select
            id="point_of_sale_id"
            name="point_of_sale_id"
            value={formData.point_of_sale_id}
            onChange={handleChange}
            required
            disabled={isPOSDisabled()}
          >
            <option value="">Seleccione un punto de venta</option>
            {pointsOfSale.map(pos => (
              <option key={pos.id} value={pos.id}>
                {pos.pos_name}
              </option>
            ))}
          </select>
          {loadingPointsOfSale && <div className="loading-indicator">Cargando puntos de venta...</div>}
          {supervisorInfo && !supervisorInfo.is_general_supervisor && (
            <div className="info-text">Punto de venta fijo para supervisor específico</div>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="sale_date">Fecha de Venta *</label>
          <input
            type="date"
            id="sale_date"
            name="sale_date"
            value={formData.sale_date}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="total_amount">Monto Total *</label>
          <div className="input-with-icon">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              id="total_amount"
              name="total_amount"
              value={formData.total_amount}
              onChange={handleChange}
              step="0.01"
              min="0.01"
              placeholder="0.00"
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">Detalle de la Venta (opcional)</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            placeholder="Registra los productos o servicios separados por coma ej: GloboMetalico, CuadernoDeRayas, BlusaBlancaS, VelaBautizo"
          />
        </div>
        <div className="form-group">
          <label htmlFor="payment_method">Método de Pago *</label>
          <div className="payment-method-options">
            <div className={`payment-option ${formData.payment_method === 'Efectivo' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_cash"
                name="payment_method"
                value="Efectivo"
                checked={formData.payment_method === 'Efectivo'}
                onChange={handleChange}
                required
              />
              <label htmlFor="payment_cash">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="6" width="20" height="12" rx="2" />
                  <circle cx="12" cy="12" r="2" />
                  <path d="M6 12h.01M18 12h.01" />
                </svg>
                Efectivo
              </label>
            </div>
            <div className={`payment-option ${formData.payment_method === 'Tarjeta' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_card"
                name="payment_method"
                value="Tarjeta"
                checked={formData.payment_method === 'Tarjeta'}
                onChange={handleChange}
              />
              <label htmlFor="payment_card">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                Tarjeta
              </label>
            </div>
            <div className={`payment-option ${formData.payment_method === 'Transferencia' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_transfer"
                name="payment_method"
                value="Transferencia"
                checked={formData.payment_method === 'Transferencia'}
                onChange={handleChange}
              />
              <label htmlFor="payment_transfer">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  <polyline points="16 3 12 7 8 3" />
                </svg>
                Transferencia
              </label>
            </div>
          </div>
        </div>
        <div className="form-group print-ticket-option">
          <label className="checkbox-container">
            <input
              type="checkbox"
              name="printTicket"
              checked={printTicket}
              onChange={handleChange}
            />
            <span className="checkbox-label">Generar Ticket de Venta (58mm)</span>
          </label>
        </div>
        <div className="form-buttons">
          <button type="button" onClick={onCancel} className="cancel-btn" disabled={loading || isGeneratingTicket}>
            Cancelar
          </button>
          <button type="submit" className="submit-btn" disabled={loading || isGeneratingTicket}>
            {loading ? 'Registrando...' : isGeneratingTicket ? 'Generando ticket...' : 'Registrar Venta'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupervisorQuickSaleForm;
